import React, { useState } from "react";
import {
    TrashIcon,
    ExclamationTriangleIcon,
    ArrowUturnLeftIcon,
    QrCodeIcon,
    ArrowPathIcon,
    MagnifyingGlassIcon,
    EllipsisVerticalIcon
} from "@heroicons/react/24/outline";

const isBookProduct = (item) => {
    if (!item) return false;
    const type = (item.product_type || item.type || "").toLowerCase();
    if (["subscription", "membership", "giftcard", "gift_card", "event", "event_ticket"].includes(type)) {
        return false;
    }
    const name = (item.name || item.title || "").toLowerCase();
    // Use word boundaries to prevent matching words like "planet" or "prevent"
    const nonBookRx = /\b(membership|gift\s*card|subscription|event|plan)\b/;
    if (nonBookRx.test(name)) {
        return false;
    }
    return true;
};

export default function CartSummary({
    items,
    onRemoveItem,
    onAddDamage,
    onRestock,
    onMissing,
    onScanReplace,
    isFulfillmentView = false,
    actionSubmitting = false,
    subtotal,
    total,
    itemCount
}) {
    const [activeDropdownId, setActiveDropdownId] = useState(null);

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4 border-b pb-2">
                Cart Summary {isFulfillmentView && `(${items.length} items)`}
            </h2>

            {items.length === 0 ? (
                <p className="text-sm text-gray-500">
                    Your cart is empty.
                </p>
            ) : (
                <div className="divide-y divide-gray-100">
                    {items.map((item, idx) => {
                        const itemImage = item.image || item.img || "https://via.placeholder.com/80x120?text=Cover";
                        const itemTitle = item.name || item.title;
                        const itemCredits = item.credits || item.price || 0;
                        const itemStatus = item.issue_or_return || item.status || "issue";
                        const itemStatusClean = String(itemStatus).toLowerCase();
                        const isBook = isBookProduct(item);
                        const itemId = item.product_id || item.id || idx;

                        return (
                            <div
                                key={itemId}
                                className="flex gap-4 py-4 first:pt-0 last:pb-0 items-center justify-between"
                            >
                                <div className="flex gap-4 items-start flex-1 min-w-0">
                                    <img
                                        src={itemImage}
                                        alt={itemTitle}
                                        className="w-12 h-18 object-cover rounded border border-gray-150 flex-shrink-0"
                                    />

                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-sm text-gray-900 truncate">
                                            {itemTitle}
                                        </h3>
                                        <p className="text-xs text-gray-400 mt-0.5">
                                            Product ID: {item.product_id || item.id || "N/A"}
                                            {isFulfillmentView && item.product_type && ` | Type: ${item.product_type}`}
                                            {isFulfillmentView && item.quantity && ` | Qty: ${item.quantity}`}
                                        </p>
                                        <div className="flex gap-2 items-center mt-2">
                                            <span className="text-xs font-bold text-blue-600">
                                                {itemCredits} Credit{Number(itemCredits) !== 1 ? "s" : ""}
                                            </span>
                                            {isFulfillmentView && (
                                                <>
                                                    <span className="text-gray-300">|</span>
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${itemStatusClean === "return" || itemStatusClean === "returning" || itemStatusClean === "returned" ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700"
                                                        }`}>
                                                        {itemStatus}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Actions Block */}
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    {isFulfillmentView ? (
                                        // Fulfillment Specific Actions
                                        (itemStatusClean !== "return" && itemStatusClean !== "returning" && itemStatusClean !== "returned" && isBook) && (
                                            <>
                                                {/* Action 1: Scan / Replace Book */}
                                                <div className="relative group">
                                                    <button
                                                        disabled={actionSubmitting}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onScanReplace?.(item);
                                                        }}
                                                        className="p-1.5 border border-gray-200 rounded-md hover:bg-black hover:text-white hover:border-black bg-white transition-all duration-150 text-gray-700 disabled:opacity-50 flex items-center justify-center shadow-xs"
                                                        title="Scan / Replace Book"
                                                    >
                                                        <QrCodeIcon className="h-4 w-4 stroke-[2]" />
                                                    </button>
                                                </div>

                                                {/* Action 2: Fulfillment Actions Dropdown */}
                                                <div className="relative group">
                                                    <button
                                                        disabled={actionSubmitting}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setActiveDropdownId(activeDropdownId === itemId ? null : itemId);
                                                        }}
                                                        className="p-1.5 border border-gray-200 rounded-md hover:bg-black hover:text-white hover:border-black bg-white transition-all duration-150 text-gray-700 disabled:opacity-50 flex items-center justify-center shadow-xs"
                                                        title="Fulfillment Actions"
                                                    >
                                                        <EllipsisVerticalIcon className="h-4 w-4 stroke-[2]" />
                                                    </button>

                                                    {activeDropdownId === itemId && (
                                                        <div className="absolute right-0 mt-2 w-36 rounded-md shadow-md bg-white border border-gray-200 z-50 py-1 divide-y divide-gray-100 text-[10px]">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setActiveDropdownId(null);
                                                                    onAddDamage?.(item);
                                                                }}
                                                                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-red-50 text-red-600 font-semibold transition-colors text-left"
                                                            >
                                                                <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-shrink-0 text-red-500" />
                                                                <span>Damaged</span>
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setActiveDropdownId(null);
                                                                    onRestock?.(item);
                                                                }}
                                                                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 text-gray-700 font-semibold transition-colors text-left"
                                                            >
                                                                <ArrowPathIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-500" />
                                                                <span>Restock</span>
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setActiveDropdownId(null);
                                                                    onMissing?.(item);
                                                                }}
                                                                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-amber-50 text-amber-700 font-semibold transition-colors text-left"
                                                            >
                                                                <MagnifyingGlassIcon className="h-3.5 w-3.5 flex-shrink-0 text-amber-600" />
                                                                <span>Missing</span>
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setActiveDropdownId(null);
                                                                    onRemoveItem?.(item);
                                                                }}
                                                                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-red-50 text-red-600 font-semibold transition-colors text-left"
                                                            >
                                                                <ArrowUturnLeftIcon className="h-3.5 w-3.5 flex-shrink-0 text-red-500" />
                                                                <span>Remove Item</span>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        )
                                    ) : (
                                        // Standard Checkout Actions
                                        <>
                                            {itemStatus === "Returning" && (
                                                <button
                                                    onClick={() => onAddDamage(item)}
                                                    className="text-gray-400 hover:text-red-500"
                                                    title="Report Damage"
                                                >
                                                    <ExclamationTriangleIcon className="h-5 w-5" />
                                                </button>
                                            )}

                                            <button
                                                onClick={() => onRemoveItem(item)}
                                                className="text-gray-400 hover:text-red-500"
                                                title="Remove from Cart"
                                            >
                                                <TrashIcon className="h-5 w-5" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
